// src/pages/expirePassword/expirePassword.tsx

import React, { FC, useState } from 'react'
import { processErrorMsg, processUserNameChange } from '../../utils'
import {
  BaseLayout,
  ButtonContainer,
  CustomAlert,
  Loader,
  Module,
  ModuleHeader,
  ModuleBody,
  CustomCheckbox,
  AutoCompleteContainer,
} from '../../components/'
import {
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
} from '@mui/material'
import Button from '@mui/material/Button'
import useStyles from './expire-password-style'

import { useOktaAuth } from '@okta/okta-react'

const ExpirePassword: FC = () => {
  const [expirePasswordUserNameSearch, setExpirePasswordUserNameSearch] =
    useState('')
  const [expirePasswordUser, setExpirePasswordUser] = useState<any | null>(null)
  const [autoCompleteOpen, setAutoCompleteOpen] = useState(false)
  const [filteredUserList, setFilteredUserList] = useState<Array<string>>([])
  const [lastUserSearch, setLastUserSearch] = useState('')
  const [lastUserSearchResults, setLastUserSearchResults] = useState([])
  const [errorAlertMessage, setErrorAlertMessage] = useState('')
  const [successAlertMessage, setSuccessAlertMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [hideLoader, setHideLoader] = useState(true)
  const [passwordExpirationConfirmed, setPasswordExpirationConfirmed] =
    useState(false)

  const USER_SEARCH_LIMIT = 200

  const { classes } = useStyles()

  const { authState } = useOktaAuth()

  const onChangeSelection = (
    event: React.ChangeEvent<{}>,
    value: any | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<string> | undefined,
  ) => {
    let newValue = {}
    if (value != null) {
      newValue = value
    }
    setExpirePasswordUser(newValue)
  }

  const userNameChange = (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: string,
  ) => {
    const {
      accessToken: { accessToken },
    } = authState

    processUserNameChange(
      reason,
      value,
      lastUserSearch,
      lastUserSearchResults,
      USER_SEARCH_LIMIT,
      accessToken,
      setExpirePasswordUserNameSearch,
      setFilteredUserList,
      setAutoCompleteOpen,
      setLastUserSearch,
      setLoading,
      setLastUserSearchResults,
      setErrorAlertMessage,
      processErrorMsg,
    )
    setErrorAlertMessage('')
    setSuccessAlertMessage('')
    setErrorAlertMessage('')
  }

  const expirePassword = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (expirePasswordUser != null) {
      setHideLoader(false)
      setPasswordExpirationConfirmed(false)

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: authState?.accessToken.accessToken,
        },
        body: `{"userId": "${expirePasswordUser.memberMlsId}","id": "${expirePasswordUser.id}"}`,
      }
      const url =
        process.env.REACT_APP_OKTA_BRIGHT_ORG_ENDPOINT + '/expirepassword'

      fetch(url, requestOptions)
        .then(async (response) => {
          const data = await response.json()

          // check for error response
          if (!response.ok) {
            // get error message from body or default to response status
            const error = (data && data.message) || response.status
            return Promise.reject(error)
          }

          setHideLoader(true)
          setSuccessAlertMessage('Password Expiration Succeeded')
        })
        .catch((error) => {
          setErrorAlertMessage('Password Expiration Failed. Error: ' + error)
          processErrorMsg(
            error,
            'ExpirePassword',
            `Update expirePassword user. Unable to process api ${url}`,
          )
          setHideLoader(true)
        })
    }
  }

  const handleExpirePasswordConfirmation = (event: any) => {
    setPasswordExpirationConfirmed(event.target.checked)
  }

  return (
    <BaseLayout>
      <Loader visible={!hideLoader} />
      <Module>
        <ModuleHeader title="Expire Password">
          <CustomAlert severity="error">{errorAlertMessage}</CustomAlert>
          <CustomAlert severity="success">{successAlertMessage}</CustomAlert>
        </ModuleHeader>

        <ModuleBody>
          <AutoCompleteContainer
            className={classes.autoCompleteTextInput}
            autoCompleteOpen={autoCompleteOpen}
            hideLoader={hideLoader}
            userNameChange={userNameChange}
            onChangeSelection={onChangeSelection}
            setAutoCompleteOpen={setAutoCompleteOpen}
            filteredUserList={filteredUserList}
            searchText={expirePasswordUserNameSearch}
            isLoading={loading}
            labelText="Find Account:"
          />

          <CustomCheckbox
            checked={passwordExpirationConfirmed}
            onChange={handleExpirePasswordConfirmation}
            label="Select Box and Click Go to Force Password Expiration"
            className={classes.checkBox}
          />
          <ButtonContainer
            primary={
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={expirePassword}
                disabled={
                  expirePasswordUser == null ||
                  !hideLoader ||
                  !passwordExpirationConfirmed
                }
              >
                Go
              </Button>
            }
          />
        </ModuleBody>
      </Module>
    </BaseLayout>
  )
}

export default ExpirePassword
